/**
 * Joint Commission Water Management Compliance Resource Page — B2B SEO magnet.
 *
 * Targets high-intent facility-management / infection-prevention queries:
 *   • "Joint Commission water management"
 *   • "Joint Commission EC.02.05.01"
 *   • "hospital water management program Legionella"
 *   • "ASHRAE 188 hospital compliance"
 *   • "CMS water management memo QSO-17-30"
 */
import SEO, { technicalArticleSchema } from "@/components/SEO";
import SiteHeader from "@/components/SiteHeader";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import { CheckCircle2, Droplets, FileText, ShieldCheck, ArrowRight, AlertTriangle, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

const WMP_ELEMENTS = [
  { title: "Water-System Assessment", text: "Map every water source, treatment step, storage vessel, and delivery point in the building. Identify potable, non-potable, and utility loops." },
  { title: "Control Measures", text: "Define disinfectant residuals, temperature ranges, and flow requirements at each control point to suppress Legionella and other waterborne pathogens." },
  { title: "Monitoring Plan", text: "Written schedule for routine testing (temperature, disinfectant residual, biological, Legionella cultures) with defined action thresholds." },
  { title: "Corrective Actions", text: "Documented remediation protocols when a control measure fails — flushing, superheat-flush, chemical shock, or engineering upgrade." },
  { title: "Program Documentation", text: "Written WMP, logs, meeting minutes of the multi-disciplinary water team, and evidence of annual program review." },
  { title: "Validation & Verification", text: "Confirm the WMP is effective through outcome measurements — case surveillance, environmental sampling, and periodic third-party audit." },
];

const FAQS = [
  {
    q: "What is Joint Commission EC.02.05.01?",
    a: "EC.02.05.01 is the Joint Commission Environment of Care standard requiring hospitals to manage utility systems — including water — to reduce infection risk. Element of Performance 14 specifically mandates a written water management program (WMP) modeled on ASHRAE 188 and CMS QSO-17-30.",
  },
  {
    q: "What is the CMS QSO-17-30 memo?",
    a: "QSO-17-30 (issued 2017, revised 2018) is the CMS survey memorandum requiring all Medicare-certified hospitals and long-term care facilities to develop and implement water management policies and procedures to reduce Legionella and other waterborne pathogen risk. Non-compliance is a Condition of Participation (CoP) finding.",
  },
  {
    q: "How does ASHRAE 188 fit in?",
    a: "ANSI/ASHRAE 188 — 'Legionellosis: Risk Management for Building Water Systems' — is the technical standard Joint Commission and CMS reference for what a compliant WMP looks like. It defines the seven WMP elements (system assessment, control measures, monitoring, corrective actions, documentation, validation, program review).",
  },
  {
    q: "Do outpatient clinics and ASCs need a WMP?",
    a: "Yes. CMS extended WMP expectations to ambulatory surgery centers and other outpatient facilities. Any facility with immunocompromised patients, showers, decorative fountains, ice machines, or complex plumbing needs a documented WMP — surveyors will ask for it.",
  },
  {
    q: "How does Sterile Mate help with Joint Commission WMP compliance?",
    a: "Sterile Mate performs on-site WMP assessments, builds custom WMP documents modeled on ASHRAE 188 + CMS QSO-17-30, provides quarterly Legionella and heterotrophic-plate-count testing, and delivers remediation services (superheat-flush, chemical shock, POU filtration) across Texas, Louisiana, Alabama, and Mississippi.",
  },
];

export default function JointCommissionWaterPage() {
  const canonical = "/resources/joint-commission-water-management";
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      technicalArticleSchema({
        headline: "Joint Commission Water Management Program (EC.02.05.01) — Compliance Guide",
        description:
          "Complete Joint Commission EC.02.05.01 water management program compliance guide. Covers ASHRAE 188 + CMS QSO-17-30 alignment, Legionella risk mitigation, WMP documentation, and Gulf-region audit services.",
        url: canonical,
        datePublished: "2026-01-25",
        dateModified: "2026-02-24",
        keywords: [
          "Joint Commission water management",
          "Joint Commission EC.02.05.01",
          "CMS QSO-17-30 water management",
          "ASHRAE 188 hospital compliance",
          "hospital Legionella program",
          "hospital water management program",
          "waterborne pathogen risk assessment",
        ],
      }),
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white" data-testid="joint-commission-water-page">
      <SEO
        canonical={canonical}
        title="Joint Commission Water Management Program (EC.02.05.01) — Compliance Guide"
        description="Complete Joint Commission EC.02.05.01 water management program compliance guide. ASHRAE 188, CMS QSO-17-30, Legionella risk, WMP documentation, and Gulf-region audit services."
        keywords={[
          "Joint Commission water management",
          "Joint Commission EC.02.05.01",
          "CMS QSO-17-30",
          "ASHRAE 188 hospital compliance",
          "hospital Legionella program",
        ]}
        schema={schema}
      />
      <SiteHeader />

      {/* HERO */}
      <section className="relative bg-gradient-to-br from-[#0a1a35] via-[#0b2447] to-[#0056A8] text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(34,211,238,0.18),transparent_60%)]" />
        <div className="relative max-w-6xl mx-auto px-6 py-20">
          <p className="text-[10px] font-bold tracking-[0.32em] uppercase text-cyan-300 mb-4">
            Compliance Resource · Water Management Program
          </p>
          <h1 className="text-4xl md:text-6xl font-black leading-[1.05] max-w-4xl">
            Joint Commission Water Management &amp; Legionella Compliance.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-white/85 max-w-3xl leading-relaxed">
            Every U.S. hospital, LTC, and ASC needs a documented Water Management Program (WMP)
            under <strong>EC.02.05.01</strong>, <strong>CMS QSO-17-30</strong>, and{" "}
            <strong>ASHRAE 188</strong>. This guide walks through the seven WMP elements and how
            we build audit-ready programs across <strong>Texas, Louisiana, Alabama, and Mississippi.</strong>
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold">
              <a href="mailto:info@sterilemate.com?subject=Water management program build">
                <ClipboardCheck className="w-5 h-5 mr-2" />
                Build a WMP for My Facility
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
              <Link to="/resources/hemodialysis-water-treatment">
                <FileText className="w-5 h-5 mr-2" />
                See Dialysis Water Standard
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* SEVEN ELEMENTS */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-black text-slate-900 mb-2">Seven Elements of a Compliant WMP</h2>
        <p className="text-slate-600 mb-8 max-w-2xl">
          Every element below must be documented, reviewed annually, and produceable within 24 hours of a Joint Commission or CMS surveyor request.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          {WMP_ELEMENTS.map((el, i) => (
            <div key={el.title} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center shrink-0 font-black text-blue-700">
                  {i + 1}
                </div>
                <div>
                  <p className="font-bold text-slate-900 mb-1">{el.title}</p>
                  <p className="text-sm text-slate-600 leading-relaxed">{el.text}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-6 text-xs text-slate-500">
          References: ANSI/ASHRAE 188-2018, CMS QSO-17-30 (Rev.), Joint Commission EC.02.05.01 EP 14.
        </p>
      </section>

      {/* HOW WE HELP */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-black text-slate-900 mb-2">How Sterile Mate Delivers WMP Compliance</h2>
          <p className="text-slate-600 mb-8 max-w-2xl">
            Water team meeting facilitation, program documentation, quarterly testing, and
            emergency remediation — one Houston-based dispatch center.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                icon: ClipboardCheck,
                title: "WMP Build & Audit",
                text: "Multi-disciplinary team facilitation, custom WMP document, and audit-ready binder aligned to ASHRAE 188 + QSO-17-30.",
              },
              {
                icon: Droplets,
                title: "Legionella & HPC Testing",
                text: "Quarterly Legionella cultures, heterotrophic plate counts, and disinfectant-residual monitoring with certified lab partners.",
              },
              {
                icon: ShieldCheck,
                title: "Remediation on Standby",
                text: "Superheat-flush, chemical shock, POU/POE filtration installation, and cooling-tower disinfection — dispatched from Houston.",
              },
            ].map((f) => (
              <div key={f.title} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <f.icon className="w-6 h-6 text-blue-700 mb-3" />
                <p className="font-bold text-slate-900 mb-1">{f.title}</p>
                <p className="text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-black text-slate-900 mb-8">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <details
              key={i}
              className="group rounded-xl border border-slate-200 bg-white p-5 open:shadow-md transition-shadow"
            >
              <summary className="cursor-pointer flex items-center justify-between font-bold text-slate-900">
                {f.q}
                <ArrowRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform" />
              </summary>
              <p className="mt-3 text-slate-700 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Warning banner */}
      <section className="bg-amber-50 border-y border-amber-200">
        <div className="max-w-4xl mx-auto px-6 py-8 flex items-start gap-4">
          <AlertTriangle className="w-6 h-6 text-amber-700 shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-amber-900 mb-1">
              No WMP? Expect a Condition-Level CMS finding.
            </p>
            <p className="text-sm text-slate-700 leading-relaxed">
              QSO-17-30 was elevated in scope in 2018 — facilities without a documented,
              implemented, and reviewed WMP receive Condition of Participation findings that
              threaten Medicare certification.{" "}
              <a href="mailto:info@sterilemate.com?subject=Water management program build" className="font-semibold text-amber-800 underline">
                Start a WMP build today
              </a>{" "}
              — most Gulf-region facilities can be audit-ready in 30-45 days.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
